/**
 * Utilidades para manejo dinámico de títulos de página
 */

const DEFAULT_META_DESCRIPTION = 'meta[name="description"]';
const DEFAULT_META_TITLE = 'meta[name="title"]';

const upsertMetaTag = (selector, attributes, content) => {
  let metaTag = document.querySelector(selector);

  if (!metaTag) {
    metaTag = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => {
      metaTag.setAttribute(key, value);
    });
    document.head.appendChild(metaTag);
  }

  metaTag.content = content;
};

export const sanitizeSeoText = (value) => {
  if (typeof value !== "string") return "";

  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

/**
 * Extrae el contenido del meta tag title de la página actual
 * @returns {string} El contenido del meta tag title o string vacío
 */
export const getMetaTitleContent = () => {
  const metaTitle = document.querySelector(DEFAULT_META_TITLE);
  return metaTitle ? metaTitle.content : '';
};

/**
 * Actualiza el título de la pestaña del navegador dinámicamente
 * @param {string} currentPath - La ruta actual
 * @param {object} tenantStyles - Los estilos del tenant actual
 */
export const setPageTitle = (currentPath, tenantStyles) => {
  if (!tenantStyles) return;

  const dynamicTitle = determineTitleFromTenantStyles(currentPath, tenantStyles);
  applySeoTags({
    title: dynamicTitle,
    description: sanitizeSeoText(tenantStyles?.description || ''),
  });
};

/**
 * Determina el título basado en la ruta actual y tenantStyles
 * @param {string} currentPath - La ruta actual
 * @param {object} tenantStyles - Los estilos del tenant
 * @returns {string} El título calculado
 */
export const determineTitleFromTenantStyles = (currentPath, tenantStyles) => {
  if (!tenantStyles || !tenantStyles.pageTitle) {
    return document.title; // Mantener título actual si no hay datos
  }

  // Extraer keyword dinámica de la ruta
  const dynamicKeyword = getDynamicKeywordFromPath(currentPath, tenantStyles);
  
  if (dynamicKeyword) {
    return `${dynamicKeyword} | ${tenantStyles.pageTitle}`;
  }

  // Si hay keywords estáticas, usar la primera
  if (tenantStyles.keywords && tenantStyles.keywords.length > 0) {
    return `${tenantStyles.keywords[0]} | ${tenantStyles.pageTitle}`;
  }

  // Fallback al título base del tenant
  return tenantStyles.pageTitle;
};

export const getTitleWithBase = (title, tenantStyles) => {
  const cleanTitle = sanitizeSeoText(title);
  const baseTitle = sanitizeSeoText(tenantStyles?.pageTitle || '');

  if (!cleanTitle) {
    return baseTitle || document.title;
  }

  return cleanTitle;
};

export const applySeoTags = ({ title, description }) => {
  const cleanTitle = sanitizeSeoText(title);
  const cleanDescription = sanitizeSeoText(description);

  if (cleanTitle) {
    document.title = cleanTitle;
    upsertMetaTag(DEFAULT_META_TITLE, { name: "title" }, cleanTitle);
    upsertMetaTag('meta[property="og:title"]', { property: "og:title" }, cleanTitle);
    upsertMetaTag('meta[name="twitter:title"]', { name: "twitter:title" }, cleanTitle);
  }

  if (cleanDescription) {
    upsertMetaTag(DEFAULT_META_DESCRIPTION, { name: "description" }, cleanDescription);
    upsertMetaTag('meta[property="og:description"]', { property: "og:description" }, cleanDescription);
    upsertMetaTag('meta[name="twitter:description"]', { name: "twitter:description" }, cleanDescription);
  }
};

/**
 * Extrae una keyword dinámica basada en la ruta actual
 * @param {string} currentPath - La ruta actual
 * @param {object} tenantStyles - Los estilos del tenant
 * @returns {string|null} La keyword extraída o null
 */
export const getDynamicKeywordFromPath = (currentPath, tenantStyles) => {
  if (!currentPath) return null;

  // Rutas específicas con keywords predefinidas
  const routeKeywords = {
    '/search': 'Búsqueda',
    '/catalogue': 'Catálogo',
    '/live': 'En directo',
    '/favorites': 'Favoritos',
    '/profile': 'Perfil',
    '/settings': 'Configuración'
  };

  // Verificar rutas exactas
  if (routeKeywords[currentPath]) {
    return routeKeywords[currentPath];
  }

  // Extraer de rutas de sección (/section/:name)
  const sectionMatch = currentPath.match(/^\/section\/(.+)$/);
  if (sectionMatch) {
    const sectionName = decodeURIComponent(sectionMatch[1]);
    // Capitalizar primera letra
    return sectionName.charAt(0).toUpperCase() + sectionName.slice(1);
  }

  // Extraer de rutas con parámetros dinámicos
  const pathSegments = currentPath.split('/').filter(segment => segment);
  if (pathSegments.length > 0) {
    const lastSegment = pathSegments[pathSegments.length - 1];
    // Si no es un número (ID), usarlo como keyword
    if (isNaN(lastSegment)) {
      return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1);
    }
  }

  return null;
};

/**
 * Actualiza el meta tag title con nuevo contenido
 * @param {string} content - El nuevo contenido para el meta tag
 */
export const updateMetaTitleContent = (content) => {
  upsertMetaTag(DEFAULT_META_TITLE, { name: 'title' }, sanitizeSeoText(content));
};

/**
 * Inicializa el sistema de títulos dinámicos
 * @param {string} currentPath - La ruta actual
 * @param {object} tenantStyles - Los estilos del tenant
 */
export const initializeDynamicTitle = (currentPath, tenantStyles) => {
  setPageTitle(currentPath, tenantStyles);
};
